import React, { useState, useEffect, useCallback } from 'react';
import { FiSave, FiLogOut, FiUser, FiLock, FiSliders, FiPlus, FiEdit2, FiTrash2, FiRefreshCw, FiCode, FiX } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import api from '../services/AxiosService';

const Settings = () => {
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const [profile, setProfile] = useState({ name: user.name || '', email: user.email || '' });
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  // Setting Collection state
  const [settingsList, setSettingsList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'add' | { edit: setting }
  const [form, setForm] = useState({ name: '', settingJson: '{\n  "version": "1.0.0"\n}' });

  const loadSettingsList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await api.get('/setting/all');
      const list = Array.isArray(res) ? res : (res.data || []);
      setSettingsList(list);
    } catch (err) {
      console.error('Failed to load settings collection:', err);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettingsList();
  }, [loadSettingsList]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    history.push('/login');
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      localStorage.setItem('admin_user', JSON.stringify({ ...user, name: profile.name, email: profile.email }));
      alert('Profile updated successfully');
    } catch (err) { alert('Failed'); }
    finally { setSaving(false); }
  };

  const openAddSetting = () => {
    setForm({ name: '', settingJson: '{\n  "version": "1.0.0",\n  "forceUpdate": false\n}' });
    setModal('add');
  };

  const openEditSetting = (item) => {
    setForm({
      name: item.name || '',
      settingJson: JSON.stringify(item.setting || {}, null, 2),
    });
    setModal({ edit: item });
  };

  const handleSaveSetting = async () => {
    if (!form.name.trim()) { alert('Setting name is required'); return; }
    let parsedObj = {};
    try {
      parsedObj = JSON.parse(form.settingJson);
    } catch (err) {
      alert('Invalid JSON format! Please fix syntax errors before saving.');
      return;
    }

    try {
      await api.put('/setting/global/update', {
        name: form.name.trim(),
        setting: parsedObj,
      });
      setModal(null);
      loadSettingsList();
    } catch (err) {
      alert('Failed to save setting: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteSetting = async (id) => {
    if (!window.confirm('Delete this setting record permanently?')) return;
    try {
      await api.delete(`/setting/${id}`);
      loadSettingsList();
    } catch (err) {
      alert('Failed to delete setting');
    }
  };

  return (
    <div className="space-y-6 max-w-5xl text-left">
      <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Settings & Configuration</h1>
        <p className="text-xs font-medium text-slate-500 mt-1">Manage admin profile, security, and MongoDB Setting collection</p>
      </div>

      {/* MongoDB Setting Collection Manager Card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center border border-purple-100">
              <FiSliders className="w-5 h-5 text-purple-700" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">MongoDB Setting Collection</h3>
              <p className="text-xs text-slate-500 font-medium">Manage app_version, payout rates, and platform configuration documents</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={loadSettingsList} className="p-2.5 rounded-xl bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition shadow-sm" title="Refresh">
              <FiRefreshCw className="w-4 h-4" />
            </button>
            <button onClick={openAddSetting} className="px-4 py-2.5 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition flex items-center gap-1.5 shadow-sm">
              <FiPlus className="w-4 h-4" /> Add Setting Record
            </button>
          </div>
        </div>

        {listLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-purple-600/30 border-t-purple-600 rounded-full animate-spin" />
          </div>
        ) : settingsList.length === 0 ? (
          <div className="text-center py-10 text-xs font-bold text-slate-500">No records found in Setting collection</div>
        ) : (
          <div className="space-y-3">
            {settingsList.map((item) => (
              <div key={item._id} className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-purple-900 uppercase tracking-wide bg-purple-100 border border-purple-200 px-2.5 py-0.5 rounded-md">
                      {item.name}
                    </span>
                    <span className="text-[10px] text-slate-500 font-medium">Updated {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '-'}</span>
                  </div>
                  <pre className="text-[11px] text-slate-800 font-mono bg-white border border-slate-200 rounded-lg p-2.5 max-h-28 overflow-y-auto">
                    {JSON.stringify(item.setting, null, 2)}
                  </pre>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => openEditSetting(item)} className="p-2 rounded-lg bg-white border border-slate-300 text-slate-600 hover:text-purple-700 transition" title="Edit Setting">
                    <FiEdit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeleteSetting(item._id)} className="p-2 rounded-lg bg-white border border-slate-300 text-slate-600 hover:text-red-600 transition" title="Delete Setting">
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profile Section */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 shadow-sm">
        <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
          <FiUser className="w-5 h-5 text-purple-700" />
          <h3 className="text-sm font-bold text-slate-900">Profile Details</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Name</label>
            <input type="text" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium" />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Email</label>
            <input type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium" />
          </div>
        </div>
        <button onClick={handleSaveProfile} disabled={saving} className="px-5 py-2.5 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition flex items-center gap-2 shadow-sm">
          <FiSave className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>

      {/* Security Section */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 shadow-sm">
        <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
          <FiLock className="w-5 h-5 text-amber-600" />
          <h3 className="text-sm font-bold text-slate-900">Security</h3>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Current Password</label>
            <input type="password" value={passwords.current} onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">New Password</label>
              <input type="password" value={passwords.newPass} onChange={e => setPasswords(p => ({ ...p, newPass: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Confirm Password</label>
              <input type="password" value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium" />
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white border border-rose-200 rounded-2xl p-6 shadow-sm">
        <h3 className="text-sm font-bold text-rose-800 mb-3">Danger Zone</h3>
        <button onClick={handleLogout} className="px-5 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold hover:bg-rose-100 transition flex items-center gap-2 shadow-sm">
          <FiLogOut className="w-3.5 h-3.5" /> Logout from Admin Panel
        </button>
      </div>

      {/* Setting Add/Edit Modal */}
      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 w-full max-w-lg space-y-4 max-h-[85vh] overflow-y-auto text-left shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <FiCode className="text-purple-700" /> {modal === 'add' ? 'Add Setting Record' : 'Edit Setting Record'}
              </h3>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><FiX className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Setting Key Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. app_version, payout_rates, banner_config"
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Setting Value (JSON object) *</label>
                <textarea
                  value={form.settingJson}
                  onChange={e => setForm(p => ({ ...p, settingJson: e.target.value }))}
                  placeholder='{\n  "version": "1.0.0"\n}'
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-xs text-slate-900 font-mono outline-none focus:border-purple-600 h-44 resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">Cancel</button>
              <button onClick={handleSaveSetting} className="px-4 py-2 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition">Save Record</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
